import { useEffect, useMemo, useState, type HTMLAttributes, type HTMLInputTypeAttribute } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Archive,
  ArrowLeft,
  Mail,
  RefreshCw,
  Save,
  ShieldAlert,
  Tags,
  UsersRound,
} from 'lucide-react';
import { paths } from '../lib/paths';
import { ConfirmDialog, SaveFooter } from '../components/PageTemplates';
import { useStore } from '../hooks/useStore';
import {
  archiveProfile,
  deactivateProfile,
  getContactReadiness,
  getProfileRelationships,
  normalizeProfile,
  reactivateProfile,
  updateProfile,
  updateProfileAttributes,
} from '../services/directoryService';
import { getSmsConsentStatus, recordSmsOptOut, type SmsPurposeConsentStatus } from '../services/communicationConsentService';
import {
  DEFAULT_DIRECTORY_FIELDS,
  ROLE_LABEL,
  type DirectoryStatus,
  type LeagueDirectoryField,
  type PreferredContactMethod,
  type Profile,
  type ProfileAttributeValue,
  type Role,
} from '../models';

const TABS = ['overview', 'attributes', 'relationships'] as const;
type Tab = (typeof TABS)[number];
const ALL_ROLES: Role[] = ['admin', 'scorekeeper', 'referee', 'captain', 'player', 'fan', 'staff'];

export function DirectoryProfilePage() {
  const { profileId } = useParams<{ profileId: string }>();
  const db = useStore((d) => d);
  const [tab, setTab] = useState<Tab>('overview');
  const [archiveConfirmOpen, setArchiveConfirmOpen] = useState(false);
  const profile = useMemo(
    () => db.profiles.map(normalizeProfile).find((person) => person.id === profileId),
    [db.profiles, profileId],
  );

  if (!profile) {
    return (
      <div className="card">
        <div className="empty">
          <h2 style={{ margin: 0 }}>Person not found</h2>
          <p className="muted">This profile may have been removed from the local league data.</p>
          <Link to={paths.adminDirectory} className="btn btn--primary btn--sm">Back to Directory</Link>
        </div>
      </div>
    );
  }

  const readiness = getContactReadiness(profile);
  const relationships = getProfileRelationships(profile.id, db);

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="card card--accent">
        <div className="row row--between" style={{ alignItems: 'flex-start', gap: 14 }}>
          <div style={{ minWidth: 0 }}>
            <Link to={paths.adminDirectory} className="btn btn--ghost btn--sm" style={{ marginBottom: 12 }}>
              <ArrowLeft size={13} /> Directory
            </Link>
            <div className="eyebrow">Person profile</div>
            <h1 style={{ margin: '5px 0 4px', fontSize: 30 }}>{profile.displayName}</h1>
            <div className="row" style={{ gap: 8 }}>
              <span className={`chip chip--${profile.directoryStatus === 'archived' ? 'info' : profile.directoryStatus === 'inactive' ? 'warn' : 'ok'}`}>
                {profile.directoryStatus}
              </span>
              <span className={`chip chip--${readiness.status === 'ready' ? 'ok' : readiness.status === 'warning' ? 'warn' : 'err'}`}>
                {readiness.label}
              </span>
              {profile.globalRoles.map((role) => <span key={role} className="chip">{ROLE_LABEL[role]}</span>)}
            </div>
          </div>
          <div className="row" style={{ gap: 8 }}>
            {profile.directoryStatus === 'archived' ? (
              <button className="btn btn--primary btn--sm" onClick={() => reactivateProfile(profile.id)}>
                <RefreshCw size={13} /> Reactivate
              </button>
            ) : (
              <>
                <button className="btn btn--ghost btn--sm" onClick={() => deactivateProfile(profile.id)}>
                  Deactivate
                </button>
                <button
                  className="btn btn--ghost btn--sm"
                  onClick={() => setArchiveConfirmOpen(true)}
                >
                  <Archive size={13} /> Archive
                </button>
              </>
            )}
          </div>
        </div>
      </section>

      <nav className="workspace-tabs" aria-label="Directory profile sections">
        {TABS.map((item) => (
          <button
            key={item}
            type="button"
            className={`workspace-tabs__tab ${tab === item ? 'is-active' : ''}`}
            onClick={() => setTab(item)}
            style={{ background: 'transparent', borderTop: 0, borderLeft: 0, borderRight: 0 }}
          >
            {tabLabel(item)}
          </button>
        ))}
      </nav>

      {tab === 'overview' && <OverviewTab profile={profile} />}
      {tab === 'attributes' && <AttributesTab profile={profile} />}
      {tab === 'relationships' && <RelationshipsTab relationships={relationships} />}
      <ConfirmDialog
        open={archiveConfirmOpen}
        title={`Archive ${profile.displayName}?`}
        body="Historical records will stay attached, but this person will be removed from active Directory views."
        confirmLabel="Archive person"
        tone="danger"
        onCancel={() => setArchiveConfirmOpen(false)}
        onConfirm={() => {
          archiveProfile(profile.id);
          setArchiveConfirmOpen(false);
        }}
      />
    </div>
  );
}

function OverviewTab({ profile }: { profile: Profile }) {
  const communicationConsents = useStore((db) => db.communicationConsents);
  const tournaments = useStore((db) => db.tournaments);
  const [form, setForm] = useState(() => overviewForm(profile));
  const [saved, setSaved] = useState('');

  useEffect(() => {
    setForm(overviewForm(profile));
    setSaved('');
  }, [profile.id, profile.updatedAt]);

  const emailValid = isValidEmailInput(form.email);
  const phoneValid = isValidPhoneInput(form.phone);
  const rolesValid = form.globalRoles.length > 0;
  const canSave = Boolean(form.displayName.trim()) && emailValid && phoneValid && rolesValid;
  const communicationReadiness = getContactReadiness(
    {
      ...profile,
      email: cleanedEmail(form.email),
      phone: cleanedPhone(form.phone),
      preferredContactMethod: form.preferredContactMethod,
      contactPreferences: {
        ...(profile.contactPreferences ?? {}),
        emailOptOut: !form.emailAllowed,
        inAppOptOut: !form.inAppAllowed,
      },
    },
    ['email', 'sms', 'in_app'],
  );
  const smsStatus = getSmsConsentStatus(
    { ...profile, phone: cleanedPhone(form.phone) },
    communicationConsents,
  );

  const save = () => {
    if (!canSave) return;
    const phone = cleanedPhone(form.phone);
    const now = new Date().toISOString();
    updateProfile(profile.id, {
      displayName: form.displayName,
      email: cleanedEmail(form.email),
      phone,
      bio: form.bio,
      defaultRole: primaryRole(form.globalRoles, profile.defaultRole),
      globalRoles: form.globalRoles,
      directoryStatus: form.directoryStatus,
      preferredContactMethod: form.preferredContactMethod,
      lastVerifiedAt: now,
      contactPreferences: {
        ...(profile.contactPreferences ?? {}),
        emailOptOut: !form.emailAllowed,
        inAppOptOut: !form.inAppAllowed,
        lastVerifiedAt: now,
      },
    });
    setSaved(new Date().toLocaleTimeString());
  };

  const toggleRole = (role: Role, on: boolean) => {
    const next = new Set(form.globalRoles);
    if (on) next.add(role);
    else next.delete(role);
    setForm({ ...form, globalRoles: Array.from(next) });
  };

  return (
    <div className="directory-profile-grid">
      <section className="card">
        <h3 className="card__title">Identity</h3>
        <div className="stack" style={{ marginTop: 14 }}>
          <Field label="Display name" value={form.displayName} onChange={(displayName) => setForm({ ...form, displayName })} />
          <div className="grid grid--2">
            <Field
              label="Email"
              value={form.email}
              type="email"
              autoComplete="email"
              onChange={(email) => setForm({ ...form, email: cleanEmailInput(email) })}
              error={!emailValid ? 'Enter a valid email address.' : undefined}
            />
            <Field
              label="Phone"
              value={form.phone}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              onChange={(phone) => setForm({ ...form, phone: cleanPhoneInput(phone) })}
              error={!phoneValid ? 'Enter 10 to 15 digits.' : undefined}
            />
          </div>
          <div className="grid grid--2">
            <label className="field">
              <span className="field__label">Status</span>
              <select className="select" value={form.directoryStatus} onChange={(event) => setForm({ ...form, directoryStatus: event.target.value as DirectoryStatus })}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="archived">Archived</option>
              </select>
            </label>
            <label className="field">
              <span className="field__label">Preferred contact</span>
              <select className="select" value={form.preferredContactMethod} onChange={(event) => setForm({ ...form, preferredContactMethod: event.target.value as PreferredContactMethod })}>
                <option value="email">Email</option>
                <option value="sms">Text</option>
                <option value="in_app">In app</option>
                <option value="none">None</option>
              </select>
            </label>
          </div>
          <label className="field">
            <span className="field__label">Bio / notes</span>
            <textarea className="textarea" rows={4} value={form.bio} onChange={(event) => setForm({ ...form, bio: event.target.value })} />
          </label>
        </div>
      </section>

      <section className="card">
        <h3 className="card__title">Roles</h3>
        <div className="stack" style={{ marginTop: 14 }}>
          <div className="field">
            <span className="field__label">League roles</span>
            <div className="row" style={{ gap: 6 }}>
              {ALL_ROLES.map((role) => {
                const on = form.globalRoles.includes(role);
                return (
                  <label key={role} className={`chip ${on ? 'chip--accent' : ''}`} style={{ cursor: 'pointer' }}>
                    <input type="checkbox" checked={on} onChange={(event) => toggleRole(role, event.target.checked)} style={{ marginRight: 6 }} />
                    {ROLE_LABEL[role]}
                  </label>
                );
              })}
            </div>
            {!rolesValid && <span className="field__error">Select at least one role.</span>}
          </div>
          <p className="muted">Roles control Directory filters, assignments, and League HQ access. Event-specific roles populate automatically from seasons, tournaments, teams, and crew assignments.</p>
        </div>
      </section>

      <section className="card">
        <h3 className="card__title"><Mail size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Communication</h3>
        <div className="stack" style={{ marginTop: 14 }}>
          <label className="directory-consent-row">
            <input type="checkbox" checked={form.emailAllowed} onChange={(event) => setForm({ ...form, emailAllowed: event.target.checked })} />
            <span>
              <strong>Email</strong>
              <span>Can receive league emails when an address is present.</span>
            </span>
          </label>
          <label className="directory-consent-row">
            <input type="checkbox" checked={form.inAppAllowed} onChange={(event) => setForm({ ...form, inAppAllowed: event.target.checked })} />
            <span>
              <strong>In-app messages</strong>
              <span>Can receive messages inside The Scorekeeper.</span>
            </span>
          </label>
          <label className="directory-consent-row">
            <ShieldAlert size={16} />
            <span>
              <strong>Text consent is registration-based</strong>
              <span>Admins can record opt-outs here, but opt-ins must be captured by the registrant during registration.</span>
            </span>
          </label>
          <SmsConsentStatusRow
            title="Event text messages"
            status={smsStatus.operational}
            tournaments={tournaments}
            onOptOut={() => recordSmsOptOut({ profileId: profile.id, purpose: 'operational', phone: cleanedPhone(form.phone) })}
          />
          <SmsConsentStatusRow
            title="Marketing text messages"
            status={smsStatus.marketing}
            tournaments={tournaments}
            onOptOut={() => recordSmsOptOut({ profileId: profile.id, purpose: 'marketing', phone: cleanedPhone(form.phone) })}
          />

          <div className="directory-readiness-panel">
            <span className={`chip chip--${communicationReadiness.status === 'ready' ? 'ok' : communicationReadiness.status === 'warning' ? 'warn' : 'err'}`}>
              {communicationReadiness.label}
            </span>
            <div className="directory-chip-row">
              {communicationReadiness.availableChannels.map((channel) => (
                <span key={channel} className="chip">{channel === 'sms' ? 'text' : channel.replace('_', ' ')}</span>
              ))}
            </div>
          </div>
          {communicationReadiness.issues.length > 0 && (
            <ul className="m-bullets">
              {communicationReadiness.issues.map((issue) => <li key={issue}>{issue}</li>)}
            </ul>
          )}
        </div>
      </section>

      <SaveFooter>
        <button className="btn btn--primary btn--sm" onClick={save} disabled={!canSave}>
          <Save size={13} /> Save overview
        </button>
        {saved && <span className="muted" style={{ fontSize: 12 }}>Saved {saved}</span>}
      </SaveFooter>
    </div>
  );
}

function SmsConsentStatusRow({
  onOptOut,
  status,
  title,
  tournaments,
}: {
  onOptOut: () => void;
  status: SmsPurposeConsentStatus;
  title: string;
  tournaments: Array<{ id: string; name: string }>;
}) {
  const consent = status.consent ?? status.previousConsent;
  const eventName = consent?.tournamentId
    ? tournaments.find((tournament) => tournament.id === consent.tournamentId)?.name ?? 'Unknown event'
    : undefined;
  const canOptOut = status.state === 'opted_in';
  const [confirmOpen, setConfirmOpen] = useState(false);

  return (
    <div className="directory-consent-row directory-consent-row--readonly">
      <span className={`chip chip--${status.state === 'opted_in' ? 'ok' : status.state === 'opted_out' || status.state === 'phone_changed' ? 'warn' : 'info'}`}>
        {smsStateLabel(status.state)}
      </span>
      <span>
        <strong>{title}</strong>
        <span>{status.message}</span>
        {consent && (
          <span>
            {eventName ? `${eventName} · ` : ''}
            {consent.source.replaceAll('_', ' ')} ·
            {formatConsentDate(consent.capturedAt)} · {formatPhone(consent.phone)}
          </span>
        )}
      </span>
      {canOptOut && (
        <button
          type="button"
          className="btn btn--ghost btn--sm"
          onClick={() => setConfirmOpen(true)}
        >
          Record opt-out
        </button>
      )}
      <ConfirmDialog
        open={confirmOpen}
        title={`Record opt-out for ${title.toLowerCase()}?`}
        body="This records a revocation and removes SMS availability for this consent purpose."
        confirmLabel="Record opt-out"
        tone="warn"
        onCancel={() => setConfirmOpen(false)}
        onConfirm={() => {
          onOptOut();
          setConfirmOpen(false);
        }}
      />
    </div>
  );
}

function AttributesTab({ profile }: { profile: Profile }) {
  const directoryFields = useStore((db) => db.league.directoryFields?.length ? db.league.directoryFields : DEFAULT_DIRECTORY_FIELDS);
  const [attributes, setAttributes] = useState<Record<string, string>>(() => stringifyAttributes(profile));
  const [customKey, setCustomKey] = useState('');
  const [customValue, setCustomValue] = useState('');
  const [saved, setSaved] = useState('');
  const configuredKeys = useMemo(() => new Set(directoryFields.map((field) => field.key)), [directoryFields]);

  useEffect(() => {
    setAttributes(stringifyAttributes(profile));
    setCustomKey('');
    setCustomValue('');
    setSaved('');
  }, [profile.id, profile.updatedAt]);

  const save = () => {
    updateProfileAttributes(profile.id, attributes);
    setSaved(new Date().toLocaleTimeString());
  };

  const addCustom = () => {
    const key = normalizeAttributeKey(customKey);
    if (!key || configuredKeys.has(key)) return;
    setAttributes({ ...attributes, [key]: customValue.trim() });
    setCustomKey('');
    setCustomValue('');
  };

  const customKeys = Object.keys(attributes)
    .filter((key) => !configuredKeys.has(key))
    .sort();

  const keyTaken = configuredKeys.has(normalizeAttributeKey(customKey)) || customKeys.includes(normalizeAttributeKey(customKey));

  return (
    <section className="card">
      <div className="card__head">
        <div>
          <h3 className="card__title"><Tags size={16} style={{ verticalAlign: -2, marginRight: 6 }} />Attributes</h3>
          <p className="card__sub">Default and custom profile fields use the same format. These values feed search, registration context, and future segments.</p>
        </div>
        <div className="row" style={{ alignItems: 'center', gap: 10 }}>
          <button className="btn btn--primary btn--sm" onClick={save}>
            <Save size={13} /> Save attributes
          </button>
          {saved && <span className="muted" style={{ fontSize: 12 }}>Saved {saved}</span>}
        </div>
      </div>

      <div className="directory-attribute-grid">
        {directoryFields.map((field) => (
          <AttributeInput
            key={field.key}
            field={field}
            value={attributes[field.key] ?? ''}
            onChange={(value) => setAttributes({ ...attributes, [field.key]: value })}
          />
        ))}
        {customKeys.map((key) => (
          <div key={key} className="directory-attribute-row">
            <Field
              label={humanizeAttributeKey(key)}
              value={attributes[key] ?? ''}
              onChange={(value) => setAttributes({ ...attributes, [key]: value })}
            />
            <button
              className="btn btn--ghost btn--sm"
              onClick={() => {
                const next = { ...attributes };
                delete next[key];
                setAttributes(next);
              }}
            >
              Remove
            </button>
          </div>
        ))}
      </div>

      <div className="directory-add-attribute">
        <Field label="New attribute" value={customKey} onChange={setCustomKey} placeholder="Jersey size" error={keyTaken ? 'That attribute already exists.' : undefined} />
        <Field label="Value" value={customValue} onChange={setCustomValue} placeholder="Large" />
        <button className="btn btn--ghost btn--sm" onClick={addCustom} disabled={!customKey.trim() || keyTaken}>
          Add attribute
        </button>
      </div>
    </section>
  );
}

function RelationshipsTab({ relationships }: { relationships: ReturnType<typeof getProfileRelationships> }) {
  return (
    <div className="grid grid--2">
      <RelationshipCard title="Competitions" count={relationships.tournaments.length} items={relationships.tournaments.map((item) => item.name)} />
      <RelationshipCard title="Teams" count={relationships.teams.length} items={relationships.teams.map((item) => item.name)} />
      <RelationshipCard title="Roster entries" count={relationships.rosterEntries.length} items={relationships.rosterEntries.map((item) => `${item.jerseyNumber} - ${item.status}`)} />
      <RelationshipCard title="Role assignments" count={relationships.roleAssignments.length} items={relationships.roleAssignments.map((item) => ROLE_LABEL[item.role])} />
      <RelationshipCard title="Registrations" count={relationships.registrations.length} items={relationships.registrations.map((item) => item.status)} />
      <RelationshipCard title="Crew assignments" count={relationships.gameAssignments.length + relationships.officialAssignments.length} items={[...relationships.gameAssignments.map((item) => item.position), ...relationships.officialAssignments.map((item) => item.role)]} />
      <RelationshipCard title="Waivers" count={relationships.waivers.length} items={relationships.waivers.map((item) => item.signerName)} />
      <RelationshipCard title="Payments" count={relationships.paymentIntents.length} items={relationships.paymentIntents.map((item) => item.status)} />
    </div>
  );
}

function RelationshipCard({ title, count, items }: { title: string; count: number; items: string[] }) {
  return (
    <section className="card">
      <div className="row row--between" style={{ alignItems: 'center' }}>
        <h3 className="card__title"><UsersRound size={16} style={{ verticalAlign: -2, marginRight: 6 }} />{title}</h3>
        <span className="chip">{count}</span>
      </div>
      {items.length === 0 ? (
        <p className="muted">Nothing linked yet.</p>
      ) : (
        <div className="stack" style={{ marginTop: 10 }}>
          {items.slice(0, 8).map((item, index) => (
            <div key={`${item}-${index}`} className="muted" style={{ borderBottom: '1px solid var(--surface-line)', paddingBottom: 6 }}>
              {item}
            </div>
          ))}
          {items.length > 8 && <span className="muted">+{items.length - 8} more</span>}
        </div>
      )}
    </section>
  );
}

function AttributeInput({
  field,
  value,
  onChange,
}: {
  field: LeagueDirectoryField;
  value: string;
  onChange: (value: string) => void;
}) {
  if (field.type === 'checkbox') {
    return (
      <label className="row" style={{ gap: 8, alignItems: 'center' }}>
        <input
          type="checkbox"
          checked={isCheckedAttribute(value)}
          onChange={(event) => onChange(event.target.checked ? 'true' : 'false')}
        />
        <span>{field.label}</span>
        {field.required && <span className="chip chip--warn">Required</span>}
      </label>
    );
  }

  if (field.type === 'select') {
    return (
      <label className="field">
        <span className="field__label">{field.label}{field.required ? ' *' : ''}</span>
        <select className="select" value={value} onChange={(event) => onChange(event.target.value)}>
          <option value="">Select...</option>
          {(field.options ?? []).map((option) => <option key={option} value={option}>{option}</option>)}
        </select>
      </label>
    );
  }

  return (
    <label className="field">
      <span className="field__label">{field.label}{field.required ? ' *' : ''}</span>
      <input
        className="input"
        type={field.type === 'number' || field.type === 'date' ? field.type : 'text'}
        value={value}
        onChange={(event) => onChange(event.target.value)}
      />
    </label>
  );
}

function Field({
  autoComplete,
  error,
  inputMode,
  label,
  onChange,
  placeholder,
  type = 'text',
  value,
}: {
  autoComplete?: string;
  error?: string;
  inputMode?: HTMLAttributes<HTMLInputElement>['inputMode'];
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  type?: HTMLInputTypeAttribute;
}) {
  return (
    <label className="field">
      <span className="field__label">{label}</span>
      <input
        autoComplete={autoComplete}
        className="input"
        inputMode={inputMode}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-invalid={Boolean(error)}
      />
      {error && <span className="field__error">{error}</span>}
    </label>
  );
}

function overviewForm(profile: Profile) {
  const preferences = profile.contactPreferences ?? {};
  return {
    displayName: profile.displayName,
    email: profile.email ?? '',
    phone: profile.phone ?? '',
    bio: profile.bio ?? '',
    globalRoles: profile.globalRoles,
    directoryStatus: profile.directoryStatus ?? 'active',
    preferredContactMethod: profile.preferredContactMethod ?? 'email',
    emailAllowed: !preferences.emailOptOut,
    inAppAllowed: !preferences.inAppOptOut,
  };
}

function stringifyAttributes(profile: Profile): Record<string, string> {
  return Object.fromEntries(
    Object.entries(profile.attributes ?? {}).map(([key, value]) => [key, attributeToString(value)]),
  );
}

function attributeToString(value: ProfileAttributeValue | undefined): string {
  if (value === undefined || value === null) return '';
  return String(value);
}

function isCheckedAttribute(value: string): boolean {
  return ['true', 'yes', '1', 'on'].includes(value.trim().toLowerCase());
}

function tabLabel(tab: Tab): string {
  if (tab === 'overview') return 'Overview';
  if (tab === 'attributes') return 'Attributes';
  if (tab === 'relationships') return 'Relationships';
  return 'Overview';
}

function cleanEmailInput(value: string): string {
  return value.replace(/\s+/g, '').toLowerCase();
}

function cleanedEmail(value: string): string | undefined {
  return cleanEmailInput(value).trim() || undefined;
}

function isValidEmailInput(value: string): boolean {
  const email = cleanedEmail(value);
  return !email || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function cleanPhoneInput(value: string): string {
  const trimmed = value.trim();
  const prefix = trimmed.startsWith('+') ? '+' : '';
  return `${prefix}${trimmed.replace(/\D+/g, '').slice(0, 15)}`;
}

function cleanedPhone(value: string): string | undefined {
  return cleanPhoneInput(value) || undefined;
}

function isValidPhoneInput(value: string): boolean {
  const digits = cleanPhoneInput(value).replace(/\D+/g, '');
  return digits.length === 0 || (digits.length >= 10 && digits.length <= 15);
}

function smsStateLabel(state: SmsPurposeConsentStatus['state']): string {
  if (state === 'opted_in') return 'Opted in';
  if (state === 'opted_out') return 'Opted out';
  if (state === 'phone_changed') return 'Phone changed';
  if (state === 'invalid_phone') return 'Invalid phone';
  return 'Not opted in';
}

function formatConsentDate(value?: string): string {
  if (!value) return 'Unknown date';
  return new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatPhone(value?: string): string {
  const digits = value?.replace(/\D+/g, '') ?? '';
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  if (digits.length === 11 && digits.startsWith('1')) {
    return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  return value || '-';
}

function primaryRole(roles: Role[], fallback: Role): Role {
  return roles.includes(fallback) ? fallback : roles[0] ?? fallback;
}

function normalizeAttributeKey(value: string): string {
  return value
    .trim()
    .replace(/[^a-zA-Z0-9]+(.)/g, (_, chr: string) => chr.toUpperCase())
    .replace(/^[^a-zA-Z]+/, '')
    .replace(/^([A-Z])/, (letter) => letter.toLowerCase());
}

function humanizeAttributeKey(value: string): string {
  const label = value
    .replace(/([A-Z])/g, ' $1')
    .replace(/[_-]+/g, ' ')
    .trim();
  return label ? label[0].toUpperCase() + label.slice(1) : value;
}
